import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Calculator } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Combobox } from '../ui/Combobox';
import { ReceiptPicker } from '../expenses/ReceiptPicker';
import type { Revenue, Tariff, TariffQuote, Customer, RevenueUnsettable } from '../../types/finance';
import type { Vehicle } from '../../types/vehicle';
import type { Driver } from '../../types/driver';
import type { RevenueCreateDto, RevenuePatchDto } from '../../lib/api/endpoints';
import { getTariffs, quoteTariff } from '../../lib/api/endpoints';
import { tariffFromDto, tariffQuoteFromDto } from '../../lib/api/mappers';
import { parseMontoCOP, formatearCOP } from '../../utils/expenses';
import { parseCantidad, formatearCantidad, descripcionTarifa, mensajeErrorFinanzas, aISODate } from '../../utils/finance';
import { fromLocalInputValue, toLocalInputValue } from '../../utils/trips';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Presente = CORREGIR ese ingreso. */
  revenue?: Revenue | null;
  customers: Customer[];
  vehicles: Vehicle[];
  drivers: Driver[];
  vehicleIdInicial?: string;
  tripIdInicial?: string;
  /** `true` para el Operador: el servidor le estampa su conductor. */
  driverAutomatico?: boolean;
  onRegistrar: (data: RevenueCreateDto, soporte: File | null) => Promise<unknown>;
  onCorregir: (revenueId: string, data: RevenuePatchDto) => Promise<unknown>;
}

interface FormState {
  customerId: string;
  tariffId: string;
  quantity: string;
  amount: string;
  overrideReason: string;
  occurredAt: string;
  vehicleId: string;
  driverId: string;
  reference: string;
  notes: string;
  reason: string;
}

const vacio: FormState = {
  customerId: '', tariffId: '', quantity: '', amount: '', overrideReason: '', occurredAt: '',
  vehicleId: '', driverId: '', reference: '', notes: '', reason: '',
};

function desdeRevenue(r: Revenue): FormState {
  return {
    customerId: r.customerId,
    tariffId: r.tariffId ?? '',
    quantity: r.quantity ? String(Number(r.quantity)) : '',
    amount: r.amount,
    overrideReason: '',
    occurredAt: toLocalInputValue(r.occurredAt),
    vehicleId: r.vehicleId ?? '',
    driverId: r.driverId ?? '',
    reference: (r.reference ?? '').trim(),
    notes: (r.notes ?? '').trim(),
    reason: '',
  };
}

/**
 * FE-32 — registrar un ingreso y corregirlo son el MISMO formulario, como en
 * gastos. Lo que es propio de aquí:
 *
 *  1. **La cuenta no se hace en el navegador.** Cliente → tarifa vigente a la
 *     fecha del servicio → cantidad, y el total propuesto lo devuelve
 *     `POST /tariffs/{id}/quote` mientras la persona teclea (ADR-030 §2). Las
 *     tarifas que se ofrecen son las vigentes ESE día y, si hay vehículo, las
 *     de su clase: elegir una vencida era el defecto del cuaderno.
 *  2. **El importe que manda es el que se cobra.** El campo se rellena con el
 *     propuesto y se puede cambiar; si difiere, la razón es obligatoria y queda
 *     en la bitácora del registro. Sin tarifa, el importe se teclea a mano.
 *  3. La cantidad se escribe como se lee en el tiquete (`2.360` kg) y se
 *     normaliza con `parseCantidad`; el peso NETO, el descuento de canastas lo
 *     aplica la tarifa.
 */
export function RevenueFormModal({
  isOpen, onClose, revenue, customers, vehicles, drivers,
  vehicleIdInicial, tripIdInicial, driverAutomatico = false, onRegistrar, onCorregir,
}: Props) {
  const esCorreccion = !!revenue;
  const [form, setForm] = useState<FormState>(vacio);
  const [soporte, setSoporte] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tarifas, setTarifas] = useState<Tariff[]>([]);
  const [cargandoTarifas, setCargandoTarifas] = useState(false);
  const [quote, setQuote] = useState<TariffQuote | null>(null);
  const [cotizando, setCotizando] = useState(false);
  const [errorQuote, setErrorQuote] = useState<string | null>(null);
  // El importe se rellena con el propuesto hasta que la persona lo toca.
  const importeTocado = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSoporte(null);
    setQuote(null);
    setErrorQuote(null);
    importeTocado.current = esCorreccion;
    if (revenue) {
      setForm(desdeRevenue(revenue));
      return;
    }
    setForm({ ...vacio, occurredAt: toLocalInputValue(new Date().toISOString()), vehicleId: vehicleIdInicial ?? '' });
  }, [isOpen, revenue, vehicleIdInicial, esCorreccion]);

  const set = (campo: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (campo === 'amount') importeTocado.current = true;
    setForm(f => ({ ...f, [campo]: e.target.value }));
  };

  const diaServicio = form.occurredAt ? aISODate(new Date(form.occurredAt)) : undefined;
  const claseVehiculo = vehicles.find(v => v.id === form.vehicleId)?.vehicleClass;

  // Las tarifas vigentes el día del servicio, del cliente elegido, de la clase
  // del vehículo (o sin clase). Se piden al servidor con `validAt`: el filtro de
  // vigencia es suyo, no se reimplementa aquí.
  useEffect(() => {
    if (!isOpen || !form.customerId || !diaServicio) { setTarifas([]); return; }
    let cancelled = false;
    setCargandoTarifas(true);
    getTariffs({ customerId: form.customerId, validAt: diaServicio, vehicleClass: claseVehiculo || undefined, active: true })
      .then(res => { if (!cancelled) setTarifas(res.items.map(tariffFromDto)); })
      .catch(() => { if (!cancelled) setTarifas([]); })
      .finally(() => { if (!cancelled) setCargandoTarifas(false); });
    return () => { cancelled = true; };
  }, [isOpen, form.customerId, diaServicio, claseVehiculo]);

  const tarifa = useMemo(() => tarifas.find(t => t.id === form.tariffId), [tarifas, form.tariffId]);
  const cantidadCanonica = form.quantity.trim() ? parseCantidad(form.quantity) : null;
  const necesitaCantidad = !!tarifa && tarifa.form !== 'FLAT';

  // La cotización, con un respiro de 350 ms para no pedir una por tecla.
  useEffect(() => {
    if (!tarifa) { setQuote(null); setErrorQuote(null); return; }
    if (necesitaCantidad && !cantidadCanonica) { setQuote(null); setErrorQuote(null); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCotizando(true);
      try {
        const q = tariffQuoteFromDto(await quoteTariff(tarifa.id, necesitaCantidad ? cantidadCanonica! : undefined));
        if (cancelled) return;
        setQuote(q);
        setErrorQuote(null);
        if (!importeTocado.current) setForm(f => ({ ...f, amount: q.computedAmount }));
      } catch (err) {
        if (!cancelled) { setQuote(null); setErrorQuote(mensajeErrorFinanzas(err, 'cotizar')); }
      } finally {
        if (!cancelled) setCotizando(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [tarifa, cantidadCanonica, necesitaCantidad]);

  const montoCanonico = parseMontoCOP(form.amount);
  const difiereDelPropuesto = !!quote && !!montoCanonico && Number(montoCanonico) !== Number(quote.computedAmount);

  const validar = (): string | null => {
    if (!form.customerId) return 'Elige el cliente.';
    if (!form.occurredAt) return 'Indica cuándo se prestó el servicio.';
    if (necesitaCantidad && !cantidadCanonica) return `Indica la cantidad en ${tarifa?.unit ?? 'la unidad de la tarifa'} (el peso neto del tiquete).`;
    if (!form.amount.trim()) return 'Indica el importe que se cobra.';
    if (!montoCanonico) return 'El importe no es válido. Escribe una cifra mayor que cero, por ejemplo 361.742.';
    if (!esCorreccion && difiereDelPropuesto && !form.overrideReason.trim()) {
      return `La tarifa propone ${formatearCOP(quote!.computedAmount)} y vas a cobrar ${formatearCOP(montoCanonico)}: explica por qué. Queda en la bitácora.`;
    }
    const asociado = Boolean(form.vehicleId || form.driverId || tripIdInicial || revenue?.tripId);
    if (!driverAutomatico && !asociado) return 'El ingreso tiene que colgar de un vehículo, un conductor o un viaje.';
    if (esCorreccion && !form.reason.trim()) return 'La razón de la corrección es obligatoria — queda en la bitácora.';
    return null;
  };

  const enviar = async () => {
    const problema = validar();
    if (problema) { setError(problema); return; }
    setEnviando(true);
    setError(null);
    try {
      if (esCorreccion && revenue) {
        const original = desdeRevenue(revenue);
        const cambios: RevenuePatchDto = { reason: form.reason.trim() };
        const unset: RevenueUnsettable[] = [];
        if (form.customerId !== original.customerId) cambios.customerId = form.customerId;
        if (form.tariffId !== original.tariffId) {
          if (form.tariffId) cambios.tariffId = form.tariffId; else unset.push('tariffId');
        }
        const qOriginal = revenue.quantity ?? '';
        if ((cantidadCanonica ?? '') !== qOriginal) {
          if (cantidadCanonica) cambios.quantity = cantidadCanonica; else unset.push('quantity');
        }
        if (montoCanonico && montoCanonico !== original.amount) cambios.amount = montoCanonico;
        if (form.occurredAt !== original.occurredAt) cambios.occurredAt = fromLocalInputValue(form.occurredAt);
        if (form.vehicleId !== original.vehicleId) { if (form.vehicleId) cambios.vehicleId = form.vehicleId; else unset.push('vehicleId'); }
        if (form.driverId !== original.driverId) { if (form.driverId) cambios.driverId = form.driverId; else unset.push('driverId'); }
        if (form.reference.trim() !== original.reference) { if (form.reference.trim()) cambios.reference = form.reference.trim(); else unset.push('reference'); }
        if (form.notes.trim() !== original.notes) { if (form.notes.trim()) cambios.notes = form.notes.trim(); else unset.push('notes'); }
        if (unset.length > 0) cambios.unset = unset;
        if (Object.keys(cambios).length === 1) {
          setError('No cambiaste ningún dato del ingreso.');
          setEnviando(false);
          return;
        }
        await onCorregir(revenue.id, cambios);
      } else {
        await onRegistrar({
          customerId: form.customerId,
          tariffId: form.tariffId || undefined,
          quantity: necesitaCantidad ? cantidadCanonica! : (cantidadCanonica ?? undefined),
          amount: montoCanonico!,
          overrideReason: difiereDelPropuesto ? form.overrideReason.trim() : undefined,
          occurredAt: fromLocalInputValue(form.occurredAt)!,
          vehicleId: form.vehicleId || undefined,
          driverId: form.driverId || undefined,
          tripId: tripIdInicial || undefined,
          reference: form.reference.trim() || undefined,
          notes: form.notes.trim() || undefined,
        }, soporte);
      }
      onClose();
    } catch (err) {
      setError(mensajeErrorFinanzas(err, esCorreccion ? 'corregir' : 'registrar'));
    } finally {
      setEnviando(false);
    }
  };

  const clientesOfrecibles = customers.filter(c => c.active || c.id === form.customerId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={esCorreccion ? 'Corregir ingreso' : 'Registrar ingreso'} size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} isLoading={enviando}>{esCorreccion ? 'Guardar corrección' : 'Registrar'}</Button>
        </>
      }>
      <div className="form-stack">
        {esCorreccion && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>Cambiar la tarifa, la cantidad o la fecha <strong>vuelve a cotizar</strong> en el servidor; el importe cobrado sigue mandando. Cambiar la fecha puede moverlo de viaje y de mes.</span>
          </div>
        )}

        <Select label="Cliente" value={form.customerId} onChange={e => { importeTocado.current = esCorreccion; setForm(f => ({ ...f, customerId: e.target.value, tariffId: '' })); }}
          options={[{ value: '', label: 'Selecciona el cliente…' }, ...clientesOfrecibles.map(c => ({ value: c.id, label: c.active ? c.name : `${c.name} (inactivo)` }))]} />

        <Input label="Cuándo se prestó el servicio" type="datetime-local" value={form.occurredAt} onChange={set('occurredAt')} />
        <p className="form-hint">Decide a qué mes, a qué viaje y a qué <strong>vigencia de tarifa</strong> pertenece.</p>

        {!driverAutomatico && (
          <div className="form-pair">
            <Select label="Vehículo" value={form.vehicleId} onChange={set('vehicleId')}
              options={[{ value: '', label: 'Sin vehículo' }, ...vehicles.map(v => ({ value: v.id, label: `${v.plate}${v.vehicleClass ? ` · ${v.vehicleClass}` : ''}` }))]} />
            <Select label="Conductor" value={form.driverId} onChange={set('driverId')}
              options={[{ value: '', label: 'Sin conductor' }, ...drivers.map(d => ({ value: d.id, label: d.name }))]} />
          </div>
        )}
        {driverAutomatico && !esCorreccion && (
          <Select label="Vehículo" value={form.vehicleId} onChange={set('vehicleId')}
            options={[{ value: '', label: 'Sin vehículo' }, ...vehicles.map(v => ({ value: v.id, label: v.plate }))]} />
        )}
        {driverAutomatico && <p className="form-hint">El ingreso queda a tu nombre. El vehículo conviene ponerlo: decide qué tarifas aplican.</p>}

        <Combobox label="Tarifa" value={form.tariffId} disabled={!form.customerId || cargandoTarifas}
          onChange={v => { importeTocado.current = esCorreccion; setForm(f => ({ ...f, tariffId: v })); }}
          placeholder={!form.customerId ? 'Elige primero el cliente' : cargandoTarifas ? 'Cargando tarifas…' : 'Busca por ruta, destino o servicio…'}
          emptyLabel="Sin tarifa (importe manual)"
          options={tarifas.map(t => ({ value: t.id, label: t.name, hint: descripcionTarifa(t) }))} />
        {form.customerId && diaServicio && !cargandoTarifas && tarifas.length === 0 && (
          <p className="form-hint">Este cliente no tiene tarifas vigentes el {diaServicio}{claseVehiculo ? ` para vehículos ${claseVehiculo}` : ''}. Puedes registrar el importe a mano.</p>
        )}

        {necesitaCantidad && (
          <>
            <Input label={`Cantidad (${tarifa?.unit ?? 'unidades'})`} value={form.quantity} onChange={e => { importeTocado.current = esCorreccion; setForm(f => ({ ...f, quantity: e.target.value })); }} inputMode="decimal" placeholder="2.360" />
            <p className={`form-hint${form.quantity.trim() && !cantidadCanonica ? ' form-hint--error' : ''}`}>
              {form.quantity.trim() === ''
                ? `El peso NETO del tiquete; el descuento (${tarifa?.discountPct ?? 0} %) lo aplica la tarifa.`
                : cantidadCanonica ? `Se registrarán ${formatearCantidad(cantidadCanonica, tarifa?.unit)}.` : 'No se entiende esa cantidad. Prueba con 2.360 o 2360,5.'}
            </p>
          </>
        )}

        {tarifa && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
            <Calculator size={16} style={{ flexShrink: 0, marginTop: '1px', color: 'var(--accent)' }} />
            <span>
              {cotizando && 'Cotizando…'}
              {!cotizando && errorQuote && <span style={{ color: '#EF4444' }}>{errorQuote}</span>}
              {!cotizando && !errorQuote && !quote && necesitaCantidad && 'Escribe la cantidad para ver el total propuesto.'}
              {!cotizando && quote && (
                <>
                  La tarifa propone <strong style={{ color: 'var(--text-primary)' }}>{formatearCOP(quote.computedAmount)}</strong>
                  {quote.chargeableQuantity && <> por {formatearCantidad(quote.chargeableQuantity, tarifa.unit)} cobrables</>}
                  {quote.minimumApplied && (
                    <> — <strong>aplica el mínimo</strong>
                      {tarifa.minimumThresholdQuantity && cantidadCanonica && Number(cantidadCanonica) < Number(tarifa.minimumThresholdQuantity)
                        ? ` por estar bajo ${formatearCantidad(tarifa.minimumThresholdQuantity, tarifa.unit)}`
                        : quote.baseAmount ? ` (la fórmula daba ${formatearCOP(quote.baseAmount)})` : ''}
                    </>
                  )}.
                </>
              )}
            </span>
          </div>
        )}

        <Input label="Importe que se cobra (COP)" value={form.amount} onChange={set('amount')} inputMode="decimal" placeholder="361.742" />
        <p className={`form-hint${form.amount.trim() && !montoCanonico ? ' form-hint--error' : ''}`}>
          {form.amount.trim() === '' ? 'Se rellena con lo propuesto cuando hay tarifa; puedes cambiarlo.' : montoCanonico ? `Se cobrará ${formatearCOP(montoCanonico)}.` : 'No se entiende esa cifra.'}
        </p>

        {!esCorreccion && difiereDelPropuesto && (
          <>
            <Input label="¿Por qué se cobra distinto de lo propuesto?" value={form.overrideReason} onChange={set('overrideReason')} placeholder="Acordado con el cliente por la demora en la báscula" />
            <p className="form-hint" style={{ color: '#8B5CF6' }}>Queda en la bitácora junto al propuesto y al cobrado. Así el precio que decide una persona no se pierde en un número.</p>
          </>
        )}

        <div className="form-pair">
          <Input label="Referencia (opcional)" value={form.reference} onChange={set('reference')} placeholder="Nº de tiquete o remesa" />
          <Textarea label="Observación (opcional)" value={form.notes} onChange={set('notes')} rows={2} placeholder="Segunda ruta del día" />
        </div>

        {tripIdInicial && !esCorreccion && <p className="form-hint">Se asignará a este viaje de forma explícita.</p>}

        {!esCorreccion && (
          <>
            <ReceiptPicker file={soporte} onChange={setSoporte} disabled={enviando} label="Foto del tiquete o remesa (opcional)" />
            <p className="form-hint">Puedes registrar sin foto y adjuntarla después desde el detalle.</p>
          </>
        )}

        {esCorreccion && <Input label="Razón de la corrección" value={form.reason} onChange={set('reason')} placeholder="El tiquete decía 2.472 kg, no 2.427" />}

        {error && <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{error}</p>}
      </div>
    </Modal>
  );
}
